
/**
 * ExerciseParticipant Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { CreateExerciseParticipantSchema, UpdateExerciseParticipantSchema } from '@repo/contracts';
import { ApiError } from '../errors/api-error.js';
import { logDropdownParamsInvocation } from '../utils/dropdown-params-telemetry.js';
import { withRetry } from '../utils/retry.js';

export class ExerciseParticipantService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            const body = response;
            return {
                success: true,
                data: body.data || [],
                pagination: body.pagination || {},
                uniqueValues: body.uniqueValues || {},
                message: body.message || 'Tatbikat katılımcıları getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch exercise participant list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch exercise participant');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const validation = CreateExerciseParticipantSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation(
                'Geçersiz form verisi',
                validation.error.format()
            );
        }
        const payload = validation.data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Tatbikat katılımcısı oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create exercise participant');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const validation = UpdateExerciseParticipantSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation(
                'Geçersiz form verisi',
                validation.error.format()
            );
        }
        const payload = validation.data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Tatbikat katılımcısı güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update exercise participant');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Tatbikat katılımcısı silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete exercise participant');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        try {
            if (this.api.batchDelete) {
                const response = await this.api.batchDelete(ids);
                return {
                    success: true,
                    data: response.data,
                    message: response.message || `${ids.length} tatbikat katılımcısı silindi`
                };
            }

            const results = { success: [], failed: [] };
            for (const id of ids) {
                try {
                    await this.api.delete(id);
                    results.success.push(id);
                } catch (err) {
                    results.failed.push({ id, error: err.message });
                }
            }

            return {
                success: results.failed.length === 0,
                data: results,
                message: `${results.success.length}/${ids.length} tatbikat katılımcısı silindi`
            };

        } catch (error) {
            throw this._handleError(error, 'Failed to bulk delete exercise participants');
        }
    }

    /**
     * Get exercise participants for dropdown selection
     */
    async getDropdownParams() {
        logDropdownParamsInvocation('ExerciseParticipantService');
        try {
            const response = await this.api.getAll({ pageSize: 1000 });
            const dropdownData = (response.data || []).map(d => ({
                value: d._id || d.id,
                label: d.name || ''
            }));
            return {
                success: true,
                data: dropdownData,
                message: 'Dropdown verisi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch dropdown data');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
